import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, Flex, Link, Text } from '@mollie/ui-react';

import { CATEGORY_BY_ID, type CategoryId } from '../../types';
import { usePreferences } from '../../hooks/usePreferences';
import { oneClickMessages } from '../../messages';
import { PageLayout } from '../../components/PageLayout';

type Phase = 'unsubscribed' | 'resubscribed';

interface OneClickUnsubscribePageProps {
  categoryId: CategoryId;
}

export function OneClickUnsubscribePage({ categoryId }: OneClickUnsubscribePageProps) {
  const intl = useIntl();
  const { updatePreference, restore } = usePreferences();
  const [phase, setPhase] = useState<Phase>('unsubscribed');
  const category = CATEGORY_BY_ID[categoryId];
  const categoryLabel = intl.formatMessage(category.name);

  // Fire unsubscribe on mount — RFC 8058 / one-click compliance.
  useEffect(() => {
    updatePreference(categoryId, false);
  }, [categoryId, updatePreference]);

  const handleUndo = () => {
    restore(categoryId);
    setPhase('resubscribed');
  };

  return (
    <PageLayout>
      <Box paddingBlock="space-600">
        <Flex direction="column" alignItems="center" gap="space-400">
          {phase === 'unsubscribed' ? (
            <>
              <Text className="pc-title" textAlign="center">
                <FormattedMessage {...oneClickMessages.unsubscribedTitle} />
              </Text>
              <Text variant="body-regular" color="secondary" textAlign="center">
                <FormattedMessage
                  {...oneClickMessages.unsubscribedBody}
                  values={{ category: <strong>{categoryLabel}</strong> }}
                />
              </Text>
              <Button variant="secondary" onClick={handleUndo}>
                <FormattedMessage {...oneClickMessages.undoButton} />
              </Button>
              <Link href={window.location.pathname}>
                <FormattedMessage {...oneClickMessages.manageAllLink} />
              </Link>
            </>
          ) : (
            <>
              <Text className="pc-title" textAlign="center">
                <FormattedMessage {...oneClickMessages.resubscribedTitle} />
              </Text>
              <Text variant="body-regular" color="secondary" textAlign="center">
                <FormattedMessage
                  {...oneClickMessages.resubscribedBody}
                  values={{ category: <strong>{categoryLabel}</strong> }}
                />
              </Text>
              <Button
                variant="primary"
                onClick={() => {
                  window.location.search = '';
                }}
              >
                <FormattedMessage {...oneClickMessages.manageAllLink} />
              </Button>
            </>
          )}
        </Flex>
      </Box>
    </PageLayout>
  );
}
